import { useBooking } from "@/context/BookingContext";
import { useSettings } from "@/context/SettingsContext";
import { Check } from "lucide-react";
import Seo from "@/components/Seo";
import { PAGE_META, packageSchema, faqSchema } from "@/lib/seo";

const FAQS = [
  {
    q: "How much does a gaming party cost?",
    a: "RGV Gaming party packages start at $395 for a 2-hour party and $475 for 3 hours. The Gaming Fanatic package is $525 and adds 4 gaming PCs. The Ultimate Gamer Esports package is available — contact us for current pricing. A $150 non-refundable deposit holds your date.",
  },
  {
    q: "What's included in a party package?",
    a: "Every package includes two experienced staff to run the show, pre-downloaded games (console or PC), 2 Xbox Series X, 2 PS5 Slim, 2 Nintendo Switch OLED and an Oculus Quest 3, plus T-Mobile 5G business internet so online play stays fast.",
  },
  {
    q: "What ages are the parties for?",
    a: "Most packages are for ages 6 and up. The Ultimate Gamer Esports package is for ages 10 and up.",
  },
  {
    q: "Do you travel to my location?",
    a: "Yes. RGV Gaming is a mobile service based in Mercedes, TX. We come to your home or business across McAllen, Mercedes, Edinburg, Pharr, Weslaco, Brownsville, Harlingen and the greater Rio Grande Valley.",
  },
];

export default function PartyPackages() {
  const { openBooking } = useBooking();
  const { settings } = useSettings();
  const packages = settings.packages;
  return (
    <div data-testid="packages-page">
      <Seo
        {...PAGE_META.packages}
        schema={[...packages.map((p) => packageSchema(p)), faqSchema(FAQS)]}
      />
      <section className="relative py-24 bg-gradient-to-br from-[#280000] to-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h1 className="font-display font-extrabold italic uppercase fluid-h1">
            Party <span className="text-rgv-red">Packages</span>
          </h1>
          <h2 className="mt-4 font-display uppercase italic text-xl sm:text-2xl text-rgv-gray">
            A Full-Service Gaming Party — Unique. Stress-Free. Unforgettable.
          </h2>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-rgv-gray text-lg leading-relaxed">
          Every package includes pre-downloading your favorite games ahead of time — console or PC.
          Two experienced staff run the show. We roll with a Cradlepoint E-320 router on T-Mobile 5G
          Business Internet with a Waveform QuadPro antenna, so online play stays fast no matter
          where we&rsquo;re parked.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        {packages.map((p, i) => (
          <div
            key={p.name}
            data-testid={`package-card-${i}`}
            className={`pkg-card hover-glow relative rounded-2xl border overflow-hidden p-8 ${
              p.featured
                ? "border-rgv-red bg-gradient-to-br from-[#280000] to-[#0A0A0A]"
                : "border-rgv-maroon bg-[#111]"
            }`}
          >
            {p.featured && (
              <span className="absolute top-4 right-4 bg-rgv-red text-white text-xs font-display uppercase px-3 py-1 rounded-sm tracking-wide">
                Most Popular
              </span>
            )}
            <h3 className="pkg-title font-display uppercase italic leading-tight">{p.name}</h3>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="pkg-price font-display text-rgv-red">{p.price}</span>
              <span className="text-rgv-gray text-sm">Ages {p.ages}</span>
            </div>
            <ul className="mt-6 space-y-3">
              {p.includes.map((inc) => (
                <li key={inc} className="flex items-start gap-3 text-rgv-gray">
                  <Check className="h-5 w-5 text-rgv-red shrink-0 mt-0.5" />
                  <span>{inc}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => openBooking(p.name)}
              data-testid={`package-book-btn-${i}`}
              className="btn-skew btn-red w-full py-3 mt-8 text-lg"
            >
              <span>Book Online</span>
            </button>
          </div>
        ))}
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-24 text-center">
        <p className="text-rgv-gray text-lg">
          All packages require a{" "}
          <span className="text-white font-semibold">$150 non-refundable deposit</span> to hold your date.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-24" data-testid="packages-faq">
        <h2 className="font-display uppercase italic fluid-h2 mb-8 text-center">
          Frequently Asked <span className="text-rgv-red">Questions</span>
        </h2>
        <div className="space-y-6">
          {FAQS.map((f, i) => (
            <article key={i} className="border-l-4 border-rgv-red pl-5">
              <h3 className="font-display uppercase text-xl sm:text-2xl text-white">{f.q}</h3>
              <p className="text-rgv-gray mt-2 leading-relaxed">{f.a}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
